import { db } from "./dbClient";

// ─── IncidentResponsePage.jsx ─────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import { downloadCSV } from "./exportUtils";

const INCIDENT_TYPES = ["screening_failure","missed_sar_deadline","missed_ctr_deadline","data_breach","policy_violation","system_failure","fraud_detected","other"];
const SEVERITIES = ["critical","high","medium","low"];
const STATUSES = ["open","investigating","remediation","resolved","closed"];

const SEV_COLOR = { critical:"#FF2244", high:"#FF4444", medium:"#FF8C00", low:"#F59E0B" };
const STATUS_COLOR = { open:"#FF2244", investigating:"#FF8C00", remediation:"#A855F7", resolved:"#39FF14", closed:"#4A5870" };

const emptyForm = () => ({ incident_date:"", type:"screening_failure", severity:"medium", title:"", description:"", discovered_by:"", assigned_to:"", root_cause:"", remediation_steps:"", regulatory_notification_required:false, regulatory_notified_date:"" });

function getTranslatedSeverity(sev, t) {
  const sevMap = {
    "critical": "incident.severities.critical",
    "high": "incident.severities.high",
    "medium": "incident.severities.medium",
    "low": "incident.severities.low",
  };
  return t(sevMap[sev] || sev);
}

function getTranslatedStatus(status, t) {
  const statusMap = {
    "open": "incident.statuses.open",
    "investigating": "incident.statuses.investigating",
    "remediation": "incident.statuses.remediation",
    "resolved": "incident.statuses.resolved",
    "closed": "incident.statuses.closed",
  };
  return t(statusMap[status] || status);
}

export default function IncidentResponsePage({ company, currentUser, team=[] }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [selected, setSelected] = useState(null);
  const [filterSev, setFilterSev] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (company?.id) load(); }, [company?.id]);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await db.from("compliance_incidents").select("*").eq("company_id", company.id).order("created_at", false);
      if (error) {
        console.error('[Incidents] Load error:', error.message);
        setIncidents([]);
      } else {
        setIncidents((data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (e) {
      console.error('[Incidents] Load failed:', e.message);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, company_id: company.id, status: "open", created_by: currentUser?.id };
      if (!payload.incident_date) delete payload.incident_date;
      if (!payload.regulatory_notified_date) delete payload.regulatory_notified_date;
      const { data, error } = await db.from("compliance_incidents").insert(payload);
      if (error) {
        console.error('[Incidents] Create error:', error.message);
      } else {
        const incidentId = data?.[0]?.id;
        if (incidentId) {
          await log({ action: "incident_reported", module: "incident_response", entityId: String(incidentId), entityName: form.title, meta: { severity: form.severity, type: form.type } });
        }
        setShowForm(false);
        setForm(emptyForm());
        await load();
      }
    } catch (e) {
      console.error('[Incidents] Create failed:', e.message);
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(id, status, extra={}) {
    try {
      const { data, error } = await db.from("compliance_incidents").update({ status, ...extra, resolved_date: ["resolved","closed"].includes(status) ? new Date().toISOString().slice(0,10) : undefined }).eq("id", id);
      if (error) {
        console.error('[Incidents] Update error:', error.message);
      } else {
        await log({ action: "incident_status_updated", module: "incident_response", entityId: String(id), entityName: selected?.title || id, newValue: { status } });
        setSelected(null);
        await load();
      }
    } catch (e) {
      console.error('[Incidents] Update failed:', e.message);
    }
  }

  const filtered = incidents.filter(x => (filterSev === "all" || x.severity === filterSev) && (filterStatus === "all" || x.status === filterStatus));
  const open = incidents.filter(x => !["resolved","closed"].includes(x.status));
  const critHigh = incidents.filter(x => ["critical","high"].includes(x.severity) && !["resolved","closed"].includes(x.status));
  const thisMonth = incidents.filter(x => x.status === "resolved" && x.resolved_date?.startsWith(new Date().toISOString().slice(0,7)));

  const S = {
    card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 },
    btn: (primary) => ({ background: primary ? C.accent : "transparent", border: `1px solid ${primary ? C.accent : C.border}`, borderRadius: 7, color: primary ? C.bg : C.textMid, padding: "7px 16px", cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace" }),
    input: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "7px 10px", fontSize: 12, fontFamily: "'DM Mono',monospace", width: "100%", boxSizing: "border-box" },
    label: { color: C.textMid, fontSize: 11, display: "block", marginBottom: 4 },
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <h1 style={{ color:C.text, fontFamily:"'Syne',sans-serif", fontSize:24, fontWeight:700, margin:0 }}>{t('incident.title')}</h1>
          <p style={{ color:C.textMid, margin:"4px 0 0", fontSize:13 }}>{t('incident.subtitle')}</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => downloadCSV(incidents.map(x => ({ ...x })), `incidents_${Date.now()}.csv`)} style={S.btn(false)}>{t('incident.exportCSV')}</button>
          <button onClick={() => setShowForm(true)} style={S.btn(true)}>+ {t('incident.newIncident')}</button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:24 }}>
        {[
          { label:"Open Incidents", value:open.length, color:C.danger },
          { label:"Critical / High", value:critHigh.length, color:"#FF8C00" },
          { label:"Resolved This Month", value:thisMonth.length, color:C.success },
        ].map(k => (
          <div key={k.label} style={{ ...S.card, borderTop:`2px solid ${k.color}`, textAlign:"center" }}>
            <div style={{ color:k.color, fontSize:32, fontFamily:"'Syne',sans-serif", fontWeight:700 }}>{k.value}</div>
            <div style={{ color:C.textMid, fontSize:11 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {["all",...SEVERITIES].map(s => (
          <button key={s} onClick={() => setFilterSev(s)} style={{ ...S.btn(filterSev===s), padding:"4px 12px", fontSize:11 }}>{s === "all" ? t('incident.allSeverities') : getTranslatedSeverity(s, t)}</button>
        ))}
        <div style={{ width:1, background:C.border }} />
        {["all",...STATUSES].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{ ...S.btn(filterStatus===s), padding:"4px 12px", fontSize:11 }}>{s === "all" ? t('incident.allStatuses') : getTranslatedStatus(s, t)}</button>
        ))}
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? <div style={{ color:C.textDim, textAlign:"center", padding:32 }}>Loading…</div> : filtered.length === 0 ? (
          <div style={{ color:C.textDim, textAlign:"center", padding:32 }}>No incidents found. That's a good sign.</div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr>{["Date","Title","Type","Severity","Status","Assigned To",""].map(h => (
                <th key={h} style={{ color:C.textDim, fontWeight:600, padding:"8px 10px", textAlign:"left", borderBottom:`1px solid ${C.border}`, fontSize:11 }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map(inc => (
                <tr key={inc.id} onClick={() => setSelected(inc)} style={{ cursor:"pointer", borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:"10px", color:C.textMid }}>{inc.incident_date || inc.created_at?.slice(0,10)}</td>
                  <td style={{ padding:"10px", color:C.text, fontWeight:600 }}>
                    {inc.title}
                    {inc.regulatory_notification_required && <span style={{ marginLeft:6, fontSize:9, color:C.warn, background:`${C.warn}22`, padding:"1px 5px", borderRadius:3 }}>REG NOTIFY</span>}
                  </td>
                  <td style={{ padding:"10px", color:C.textMid }}>{inc.type?.replace(/_/g," ")}</td>
                  <td style={{ padding:"10px" }}>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${SEV_COLOR[inc.severity]}22`, color:SEV_COLOR[inc.severity], fontWeight:700 }}>
                      {inc.severity === "critical" && "● "}{getTranslatedSeverity(inc.severity, t)}
                    </span>
                  </td>
                  <td style={{ padding:"10px" }}>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${STATUS_COLOR[inc.status]}22`, color:STATUS_COLOR[inc.status] }}>{getTranslatedStatus(inc.status, t)}</span>
                  </td>
                  <td style={{ padding:"10px", color:C.textMid }}>{inc.assigned_to || "—"}</td>
                  <td style={{ padding:"10px" }}>
                    {!["resolved","closed"].includes(inc.status) && (
                      <button onClick={e => { e.stopPropagation(); updateStatus(inc.id, "resolved"); }} style={{ ...S.btn(false), padding:"3px 10px", fontSize:10 }}>Resolve</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={() => setSelected(null)}>
          <div style={{ ...S.card, maxWidth:600, width:"90%", maxHeight:"85vh", overflowY:"auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16 }}>
              <div>
                <div style={{ color:C.text, fontWeight:700, fontSize:16 }}>{selected.title}</div>
                <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${SEV_COLOR[selected.severity]}22`, color:SEV_COLOR[selected.severity], fontWeight:700 }}>{getTranslatedSeverity(selected.severity, t)}</span>
                <span style={{ marginLeft:6, fontSize:10, padding:"2px 8px", borderRadius:10, background:`${STATUS_COLOR[selected.status]}22`, color:STATUS_COLOR[selected.status] }}>{getTranslatedStatus(selected.status, t)}</span>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:"transparent", border:"none", color:C.textDim, cursor:"pointer", fontSize:20 }}>×</button>
            </div>
            {[
              ["Incident Date", selected.incident_date || selected.created_at?.slice(0,10)],
              ["Type", selected.type?.replace(/_/g," ")],
              ["Description", selected.description],
              ["Discovered By", selected.discovered_by],
              ["Assigned To", selected.assigned_to],
              ["Root Cause", selected.root_cause],
              ["Remediation Steps", selected.remediation_steps],
              ["Regulatory Notification Required", selected.regulatory_notification_required ? "Yes" : "No"],
              ["Regulatory Notification Date", selected.regulatory_notified_date || "—"],
              ["Resolved Date", selected.resolved_date || "—"],
            ].map(([k,v]) => v && (
              <div key={k} style={{ marginBottom:12 }}>
                <div style={{ color:C.textDim, fontSize:10, marginBottom:3 }}>{k.toUpperCase()}</div>
                <div style={{ color:C.text, fontSize:12, whiteSpace:"pre-wrap" }}>{v}</div>
              </div>
            ))}
            <div style={{ display:"flex", gap:8, marginTop:16, flexWrap:"wrap" }}>
              {STATUSES.filter(s => s !== selected.status).map(s => (
                <button key={s} onClick={() => updateStatus(selected.id, s)} style={{ ...S.btn(s==="resolved"||s==="closed"), padding:"5px 12px", fontSize:11 }}>→ {s}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showForm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={() => setShowForm(false)}>
          <div style={{ ...S.card, maxWidth:560, width:"90%", maxHeight:"85vh", overflowY:"auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ color:C.text, fontWeight:700, fontSize:16, marginBottom:16 }}>Report Compliance Incident</div>
            <form onSubmit={handleCreate}>
              {[
                ["Incident Date", "incident_date", "date"],
                ["Title", "title", "text"],
                ["Discovered By", "discovered_by", "text"],
              ].map(([l,k,t]) => (
                <div key={k} style={{ marginBottom:12 }}>
                  <label style={S.label}>{l}</label>
                  <input type={t} required={k==="title"} value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))} style={S.input} />
                </div>
              ))}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                <div>
                  <label style={S.label}>Type</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={S.input}>
                    {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Severity</label>
                  <select value={form.severity} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))} style={S.input}>
                    {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={S.label}>Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} style={{ ...S.input, resize:"vertical" }} />
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ ...S.label, display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                  <input type="checkbox" checked={form.regulatory_notification_required} onChange={e => setForm(p => ({ ...p, regulatory_notification_required: e.target.checked }))} />
                  Regulatory Notification Required
                </label>
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
                <button type="button" onClick={() => setShowForm(false)} style={S.btn(false)}>Cancel</button>
                <button type="submit" disabled={saving} style={S.btn(true)}>{saving ? "Saving…" : "Report Incident"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
